/**
 * `publicData.combination` — the search strings a listing is indexed by.
 *
 * `combination` is a multi-enum listing field in Sharetribe Console. The plate
 * search filters on it (`pub_combination=has_any:...` / `has_all:...`), so the
 * strings built here must match the strings the search side builds from the
 * vehicle fitment data (see util/carSetups.js).
 *
 * Formats:
 *   tyres / complete wheels   "235/55R20"      width/profile R diameter
 *   rims                      "20 ET40"        diameter ET offset
 *
 * P25009-365: a staggered listing (different front and rear sizes) gets TWO
 * strings, so it is findable by both axles. Before this it carried the front
 * size only, so a rear-size search could never reach it — which in turn made
 * the set-based plate search (P25009-364, `has_all`) return nothing.
 *
 * NOTE: there is no rear-diameter field on a listing, so the rear string
 * reuses the single `diameter` value. A split-diameter fitment (19" front /
 * 21" rear) therefore cannot be represented, which is why the set search
 * excludes those cars (P25009-364 BR-5).
 *
 * TWIN FILE: server/api-util/listingCombination.js is the CommonJS copy used
 * by the package-reach publisher and the backfill script. Keep both in sync.
 */

/* `listingType` values from the hosted config, plus the short `adType`
 * values the new create page uses. Note the trap: `businesswheel`
 * (no dash) is a RIM listing, `business-wheel` (dash) is a complete wheel. */
const TIRE_TYPES = ['wheel', 'business-wheel', 'tire', 'businesstire'];
const RIM_TYPES = ['rim', 'businesswheel'];

const clean = value =>
  value === null || value === undefined ? '' : String(value).trim();

/* The vehicle API returns sizes like "20.00"; the search side strips the
 * ".00" before querying, so listings must be indexed the same way. */
const stripTrailingZeros = value => value.replace(/\.00\b/g, '');

/* Several of these are enum fields that offer an "other" option, so a
 * truthiness check is not enough — it produced index entries like
 * "265/85Rother" and "other ET9". A size that is not a number cannot match
 * anything a search builds, so it must not be indexed at all. */
const isNumeric = value => /^\d+(\.\d+)?$/.test(value);
const isSignedNumeric = value => /^-?\d+(\.\d+)?$/.test(value);

const tireString = (width, profile, diameter) =>
  isNumeric(width) && isNumeric(profile) && isNumeric(diameter)
    ? `${width}/${profile}R${diameter}`
    : '';

/* ET may legitimately be 0 or negative. */
const rimString = (diameter, et) =>
  isNumeric(diameter) && isSignedNumeric(et) ? `${diameter} ET${et}` : '';

/**
 * Build the `combination` array for one listing.
 *
 * @param {Object} fields listing values. `adType` or `listingType` selects the
 *   format; the rest are the raw publicData values (strings or numbers).
 * @returns {string[]} deduped search strings, front first. Empty when the
 *   required parts are missing — better an unindexed listing than a
 *   "undefined/undefinedRundefined" entry in the search index.
 */
export const buildCombination = (fields = {}) => {
  const type = clean(fields.adType || fields.listingType);
  const diameter = clean(fields.diameter);
  const values = [];

  if (TIRE_TYPES.includes(type)) {
    values.push(tireString(clean(fields.tireWidthFront), clean(fields.profileFront), diameter));
    values.push(tireString(clean(fields.tireWidthRear), clean(fields.profileRear), diameter));
  } else if (RIM_TYPES.includes(type)) {
    values.push(rimString(diameter, clean(fields.etFront)));
    values.push(rimString(diameter, clean(fields.etRear)));
  }

  return [...new Set(values.filter(Boolean).map(stripTrailingZeros))];
};

/** True when the listing covers two different axle sizes. */
export const isStaggeredListing = fields => buildCombination(fields).length > 1;
